/**
 * A batch of requests rate-limited together (e.g. a large upload) needs its
 * retries spread out, not fired in lockstep - otherwise they just get
 * 429'd again as a group.
 * 
 * Fires immediately like a plain fetch; only a 429 response triggers a
 * retry, after a randomized delay.
 *
 * Honors Retry-After when present; otherwise "full jitter" - a random
 * delay up to an exponentially growing ceiling.
 * See: https://aws.amazon.com/blogs/architecture/exponential-backoff-and-jitter/
 */

const MAX_RETRIES = 7
const BASE_DELAY_MS = 1000
const MAX_DELAY_MS = 60000

const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

// Releases an unread response body so it doesn't tie up the browser's
// per-origin connection pool.
const cancelBody = res => {
  if (res && res.body && res.body.cancel) res.body.cancel()
}

const getRetryDelayMs = (res, attempt) => {
  const header = res && res.headers && res.headers.get && res.headers.get("Retry-After")

  if (header) {
    // Jitter even a server-specified delay, so a batch throttled together
    // doesn't retry in lockstep. Cap it at MAX_DELAY_MS, same as the
    // exponential path, so a large/misconfigured value can't stall a retry
    // far beyond the rest of this module's budget.
    const retryAfterSeconds = Number(header)
    if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds >= 0) {
      return Math.min(retryAfterSeconds * 1000, MAX_DELAY_MS) + Math.random() * 1000
    }

    // Retry-After can also be an HTTP-date, not just delta-seconds.
    const delayMs = Date.parse(header) - Date.now()
    if (Number.isFinite(delayMs)) {
      return Math.min(Math.max(delayMs, 0), MAX_DELAY_MS) + Math.random() * 1000
    }
  }

  const ceiling = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** attempt)
  return Math.random() * ceiling
}

/**
 * Use directly only when you need the raw Response - no body to parse, a
 * non-JSON body, or you want to check `res.ok` yourself instead of a
 * thrown failure.
 * 
 * Otherwise prefer fetchJsonWithBackoff below.
 *
 * @param {Function} run - returns the Promise for the network call
 * @param {Number} attempt - internal, used for recursion. Do not pass.
 * @returns {Promise} the (eventual) response, even another 429; if
 *   retries are exhausted - callers check `res.ok` themselves.
 */
export const fetchWithBackoff = async (run, attempt = 0) => {
  const res = await run()

  if (res && res.status === 429 && attempt < MAX_RETRIES) {
    cancelBody(res)
    await wait(getRetryDelayMs(res, attempt))
    return fetchWithBackoff(run, attempt + 1)
  }

  return res
}

/**
 * Raised by fetchJsonWithBackoff on a non-ok response.
 * Carries the response for callers that need it, e.g. to read the status.
 */
export class FetchError extends Error {
  constructor(response) {
    super(`Request failed with status ${response.status}`)
    this.name = "FetchError"
    this.response = response
  }
}

/**
 * Gives callers a single place (.catch) to handle a request that could not
 * be completed: same as fetchWithBackoff, but parses the JSON body and
 * rejects with a FetchError on a non-ok response.
 *
 * @param {Function} run - returns the Promise for the network call
 * @returns {Promise} the parsed JSON body, or rejects with a FetchError
 *   (not-ok response) or whatever `run()` rejects with.
 */
export const fetchJsonWithBackoff = async (run) => {
  const res = await fetchWithBackoff(run)

  if (!res.ok) {
    cancelBody(res)
    throw new FetchError(res)
  }

  return res.json()
}
